import React, { useState, useEffect, useCallback } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import DataTable from "../component/DataTable";
import axiosInstance from "../axiosInterceptor";
import { toast } from "react-toastify";
import LightboxWrapper from "../component/LightboxWrapper";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SweetAlert from "../component/SweetAlert";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';

const ScheduleNotification = () => {
  const navigate = useNavigate();

  const [rowsData, setRowsData] = useState([]);
  const [field, setField] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [loading, setLoading] = useState(false);

  const baseColumns = [
    {
      field: "image_url",
      headerName: "Image",
      flex: 0.8,
      renderCell: (params) => {
        const thumbImage = params.value;
        if (!thumbImage) {
          return <span style={{ fontSize: "18px" }}>–</span>;
        }

        return (
          <LightboxWrapper>
            <img
              src={thumbImage}
              alt="Thumbnail"
              loading="lazy"
              style={{
                // width: 100,
                height: 100,
                objectFit: "cover",
                display: "block",
                backgroundColor: "#000",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentNode.innerHTML =
                  "<span style='font-size:18px'>–</span>";
              }}
            />
          </LightboxWrapper>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
    },
    {
      field: "scheduleDate",
      headerName: "Schedule Date",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(date)
          .replace(/am|pm/i, (m) => m.toUpperCase());
      },
    },
    {
      field: "sendStatus",
      headerName: "Send Status",
      flex: 0.6,
      renderCell: (params) => {
        const { row } = params;
        return <span className={`badge ${row.sendStatus === "Pending" ? "bg-warning" : "bg-success"}`} style={{ fontSize: "13px" }}>{row.sendStatus}</span>;
      },
    },
    {
      field: "action",
      headerName: "Type",
      flex: 0.6,
      renderCell: (params) => {
        const value = params?.value;
        return (
          <div style={{ textAlign: value == null || undefined ? "center" : "left", width: "100%" }}>
            {value == null || undefined ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "updatedAt",
      headerName: "Update Date",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(date)
          .replace(/am|pm/i, (m) => m.toUpperCase());
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <>
          {
            params.row.sendStatus === "Pending" && (
              <GridActionsCellItem
                icon={<Tooltip title="Edit">
                  <EditIcon color="primary" />
                </Tooltip>}
                label="Edit"
                showInMenu={false}
                onClick={() => handleEdit(params.id)}
              />
            )
          }
          <GridActionsCellItem
            icon={
              <Tooltip title="Clone">
                <ContentCopyIcon />
              </Tooltip>
            }
            label="Clone"
            showInMenu={false}
            onClick={() => handleClone(params.id)}
          />
          <GridActionsCellItem
            icon={
              <Tooltip title="Delete">
                <DeleteIcon color="error" />
              </Tooltip>
            }
            label="Delete"
            showInMenu={false}
            onClick={() => handleDelete(params.id)}
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(baseColumns);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`scheduleNotification/pagination?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`);

      if (data?.data) {
        setRowsData(data.data);
        setTotalRecords(data.totalRecords);
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const keys = rowsData.length > 0 ? Object.keys(rowsData[0]) : [];

  const handleAddColumn = (e) => {
    e.preventDefault();

    if (!field || !headerName) return;

    const isDuplicate = columns.some((col) => col.field === field);
    if (isDuplicate) {
      Swal.fire("Field already exists!", "", "warning");
      return;
    }

    const newColumn = {
      field: field.trim(),
      headerName: headerName.trim(),
      flex: 1,
    };

    const actionCol = columns.find((col) => col.field === "actions");
    const otherCols = columns.filter((col) => col.field !== "actions");

    setColumns([...otherCols, newColumn, actionCol]);
    setField("");
    setHeaderName("");
  };

  const handleAddClick = async () => {
    navigate("/add-schedule-notification");
  };

  const handleEdit = async (notification_id) => {
    try {
      const response = await axiosInstance.get(`scheduleNotification/getById/${notification_id}`);
      if (response?.data?.isSuccess) {
        navigate(`/edit-schedule-notification/${notification_id}`, {
          state: {
            ScheduleNotification: response?.data?.data,
          },
        });
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    }
  };

  const handleDelete = async (notification_id) => {
    try {
      const confirmed = await SweetAlert.confirmDelete();
      if (!confirmed) return;

      const response = await axiosInstance.delete(`scheduleNotification/delete/${notification_id}`);
      setRowsData((prev) => prev.filter((row) => row.id !== notification_id));
      SweetAlert.deleteSuccess();
      if (response?.data?.isSuccess) {
        fetchData();
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    }
  };

  const handleClone = async (notification_id) => {
    try {
      const confirmed = await SweetAlert.confirmClone();
      if (!confirmed) return;

      const response = await axiosInstance.get(`scheduleNotification/${notification_id}/clone`);
      SweetAlert.cloneSuccess();
      if (response?.data?.isSuccess) {
        fetchData();
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    }
  };

  return (
    <div className="content-wrapper pd-top">
      <div className="page-header mt-5">
        <h3 className="page-title">Schedule Notifications</h3>
      </div>

      {/* Column Add Form */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div style={{ width: "75%" }}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">
                <h5 className="m-0">Add Dynamic Column</h5>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <form
                onSubmit={handleAddColumn}
                className="p-3 border rounded bg-white"
              >
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label>Field</label>
                      <select
                        className="form-control"
                        value={field}
                        onChange={(e) => setField(e.target.value)}
                      >
                        <option value="" disabled>
                          Select a field
                        </option>
                        {keys.map((key, idx) => (
                          <option key={idx} value={key}>
                            {key}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label>Header Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={headerName}
                        onChange={(e) => setHeaderName(e.target.value)}
                        placeholder="e.g. Email"
                      />
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <button type="submit" className="btn btn-primary">
                    Add Column
                  </button>
                </div>
              </form>
            </AccordionDetails>
          </Accordion>
        </div>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            padding: "8px 20px",
            fontWeight: 600,
            backgroundColor: "#1976d2",
            boxShadow: "0 4px 10px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              backgroundColor: "#1565c0",
              boxShadow: "0 6px 14px rgba(25, 118, 210, 0.4)",
            },
          }}
        >
          Schedule Notification
        </Button>
      </div>

      {/* Data Table */}
      <div className="row">
        <div className="col-lg-12">
          <DataTable
            key={paginationModel.page}
            rows={rowsData}
            columns={columns}
            getRowId={(row) => row._id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalRecords}
            paginationMode="server"
            loading={loading}
            disableVirtualization
            getRowHeight={() => 100}
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleNotification;
